import { CourseDtoTypeEnum, PaymentDtoLevelEnum, PaymentDtoTypeEnum } from 'api/generated';
import { COACHESONLINE_MVP_SHORTCODE, MESO_MVP_SHORTCODE } from 'utils/handoffShortcodes';
import { MESO_SLOTS, SlotBadgeConfig } from 'utils/scarcity';

/**
 * The MVP bonuses, after certify-ipta's BonusMegaMenu (src/components/megamenu/BonusMegaMenu.tsx).
 * The slot configs must stay identical to certify's, or the "claimed" counts drift apart between sites.
 */

/**
 * Logos are served from public/ rather than imported: CRA runs imported SVGs through @svgr/webpack,
 * which picks up svgr.config.js (typescript: true, meant for the icons CLI) and emits TypeScript
 * that its own Babel step cannot parse.
 */
const logo = (file: string) => `/images/bonuses/${file}`;

export const TRAINER_ACADEMY_URL = 'https://traineracademy.org';

const MVP_PAID_TYPES: string[] = [
  PaymentDtoTypeEnum.Purchase,
  PaymentDtoTypeEnum.Subscription,
  PaymentDtoTypeEnum.Installment,
];

/**
 * An MVP purchaser: gold, and actually paid for. A free trial can be gold-level too, and must not
 * see the claim links or codes.
 */
export const isMvpPurchase = (payment?: { level?: string; type?: string } | null) =>
  payment?.level === PaymentDtoLevelEnum.Gold && !!payment.type && MVP_PAID_TYPES.includes(payment.type);

/**
 * Who gets the new bonuses banner and popup: certification courses and bundles (every bundle is
 * made of certifications — Essential / Elite / Master and the older two- and three-cert bundles).
 * Study materials keep the old banner and their own bonus manual.
 */
export const isCertificationCourse = (course?: { type?: string } | null) =>
  course?.type === CourseDtoTypeEnum.Certificate || course?.type === CourseDtoTypeEnum.Bundle;

/** Stable key per bonus (e.g. "meso"): remembers its unlocked state and names its analytics. */
export type BonusId = string;

/**
 * Partners whose claim is a sign-in link minted by our backend. The endpoints are fixed here rather
 * than editable in the admin, because the app calls them with the student's own auth token.
 */
export type HandoffPartner = 'meso' | 'coaches-online';

export const HANDOFF_PARTNERS: Record<
  HandoffPartner,
  { endpoint: string; openLabel: string; analyticsEvent: string; shortcodeUrl: string }
> = {
  meso: {
    endpoint: '/meso/handoff-url',
    openLabel: 'Open Meso',
    analyticsEvent: 'Meso Claim Pressed',
    // What an admin puts in the Unlock URL, as in the old bonus manuals.
    shortcodeUrl: `https://${MESO_MVP_SHORTCODE}/`,
  },
  'coaches-online': {
    endpoint: '/coachesonline/handoff-url',
    openLabel: 'Open Coaches Online',
    analyticsEvent: 'CoachesOnline Claim Pressed',
    shortcodeUrl: `http://${COACHESONLINE_MVP_SHORTCODE}/`,
  },
};

export type BonusSlots = SlotBadgeConfig & { maxUnits: number };

/** How an MVP member claims the bonus. Everyone else is sent to the MVP upgrade instead. */
export type BonusUnlock =
  /** A partner sign-in link minted by our backend (MVP-gated there too). */
  | {
      kind: 'handoff';
      partner: HandoffPartner;
      endpoint: string;
      openLabel: string;
      analyticsEvent: string;
      shortcodeUrl: string;
    }
  /**
   * A partner or course link that grants the bonus. With a `code`, the first click only reveals the
   * code, since it has to be entered at that link's checkout; the button then opens the link.
   */
  | { kind: 'link'; url: string; openLabel: string; code?: string; codeNote?: string };

export type Bonus = {
  id: BonusId;
  name: string;
  /** Shorter name for the Home bonuses banner tiles; falls back to `name`. */
  shortName?: string;
  logo?: string;
  /** What the logo itself says, when the name describes the bonus rather than the brand. */
  logoAlt?: string;
  logoDark?: string;
  /** Monochrome ink logo with no white variant — inverted in dark mode instead. */
  invertLogoInDark?: boolean;
  /** The CEU cap only fills the middle of its square artboard, so it needs a taller box. */
  isTallLogo?: boolean;
  blurb: string;
  value: string;
  /** Shorter value for the Home bonuses banner tiles; falls back to `value`. */
  shortValue?: string;
  detail: string;
  /** Replaces `detail` for MVP purchasers only — for claim details a free trial must not see. */
  memberDetail?: string;
  /** Partner deals carry the live counter; the rest show "Included". */
  slots?: BonusSlots;
  /** Public page behind "Visit website"; the link is hidden when empty. */
  website: string;
  unlock: BonusUnlock;
};

/**
 * The bonuses as shipped, used until a course's bonus manual has Bonus card blocks. Deliberately the
 * free-trial version: no claim links, coupons or member-only text, because this file ships in the app
 * bundle anyone can read. The MVP details live only in the bonus-section-upgrade manual
 * (backend/scripts/bonus-cards/bonus-cards.mvp.seed.json).
 */
export const PARTNER_BONUSES: Bonus[] = [
  {
    id: 'coaches-online',
    name: 'Trainer Website Builder',
    shortName: 'Website Builder',
    logoAlt: 'Coaches Online',
    logo: logo('coaches-online.svg'),
    logoDark: logo('coaches-online-white.svg'),
    blurb:
      'A client-converting trainer website, built in minutes with no tech skills — booking, packages and payments wired in.',
    value: '$499 value · Free',
    shortValue: '$499 value',
    detail: 'Site for Life deal — yours, not rented',
    slots: { maxUnits: 60, delayHours: 36 },
    website: 'https://coachesonline.com',
    unlock: { kind: 'handoff', partner: 'coaches-online', ...HANDOFF_PARTNERS['coaches-online'] },
  },
  {
    id: 'trainer-stack',
    name: 'Trainer Stack',
    logo: logo('trainer-stack.webp'),
    invertLogoInDark: true,
    blurb: 'All-Access: eight business courses on training, nutrition and actually running a book of clients.',
    value: '$799 value · Free',
    shortValue: '$799 value',
    detail: 'The part no certification teaches you',
    slots: { maxUnits: 50 },
    website: 'https://www.trainerstack.co',
    unlock: { kind: 'link', url: 'https://www.trainerstack.co', openLabel: 'Open Trainer Stack' },
  },
  {
    id: 'insurance',
    name: 'Liability Insurance',
    logo: logo('insure-fitness.png'),
    invertLogoInDark: true,
    blurb:
      'Professional liability cover through Insure Fitness Group, built for trainers working online, in-person or independently.',
    value: '$40 off',
    detail: 'Exclusive IPTA discount',
    website: 'https://insurefitness.com/',
    unlock: { kind: 'link', url: 'https://insurefitness.com/', openLabel: 'Open Insure Fitness' },
  },
];

export const MVP_BONUSES: Bonus[] = [
  {
    id: 'meso',
    name: 'Meso Core',
    shortName: 'All-in-one App',
    logo: logo('meso.svg'),
    logoDark: logo('meso-white.svg'),
    blurb:
      'Run your whole business from one app — programming, nutrition targets, check-ins and payments behind one login.',
    value: '2 years included',
    detail: 'Lifetime on the Elite and Master bundles',
    // The cap (24) sits below this threshold, so Meso never reads "Only N left" — same as certify.
    slots: { ...MESO_SLOTS, finalLowUnitsThreshold: MESO_SLOTS.maxUnits + 1 },
    website: `${TRAINER_ACADEMY_URL}/meso`,
    unlock: { kind: 'handoff', partner: 'meso', ...HANDOFF_PARTNERS.meso },
  },
  {
    id: 'cpr',
    name: 'CPR/AED Certification',
    logo: logo('cpr.png'),
    blurb:
      'Nearly every gym asks for a current card before your first shift. Done entirely online — no weekend course.',
    value: 'Included',
    detail: 'Two-year card, downloadable the day you finish',
    website: `${TRAINER_ACADEMY_URL}/cpr`,
    unlock: { kind: 'link', url: `${TRAINER_ACADEMY_URL}/cpr`, openLabel: 'Open CPR/AED course' },
  },
  {
    id: 'ceu',
    name: 'Free CEU Course',
    logo: logo('ceu.svg'),
    logoDark: logo('ceu-white.svg'),
    isTallLogo: true,
    blurb:
      'When it is time to recertify, pick any of our 100+ specialty courses and grow the exact skill your clients want.',
    value: 'Up to $299 value',
    detail: 'Recertify without paying for it',
    website: `${TRAINER_ACADEMY_URL}/ceu`,
    unlock: { kind: 'link', url: `${TRAINER_ACADEMY_URL}/ceu`, openLabel: 'Choose your course' },
  },
];

/** Everything the bonuses popup and Home banner render. */
export type BonusesContent = {
  /** Popup and banner title. */
  title: string;
  /** Upgrade button label. */
  buttonText: string;
  /** "Strategic partnerships" column, top to bottom. */
  partners: Bonus[];
  /** "Yours with the MVP" column, top to bottom. */
  ours: Bonus[];
  /** Home banner tiles, in reading order. */
  bannerTiles: Bonus[];
};

export const DEFAULT_TITLE = 'MVP Bonuses';
export const DEFAULT_BUTTON_TEXT = 'Upgrade to MVP';

const DEFAULT_BANNER_ORDER: BonusId[] = ['meso', 'coaches-online', 'trainer-stack', 'cpr', 'ceu', 'insurance'];
const DEFAULT_BONUS_BY_ID = new Map([...PARTNER_BONUSES, ...MVP_BONUSES].map((bonus) => [bonus.id, bonus]));

export const DEFAULT_BONUSES_CONTENT: BonusesContent = {
  title: DEFAULT_TITLE,
  buttonText: DEFAULT_BUTTON_TEXT,
  partners: PARTNER_BONUSES,
  ours: MVP_BONUSES,
  bannerTiles: DEFAULT_BANNER_ORDER.map((id) => DEFAULT_BONUS_BY_ID.get(id)).filter((bonus): bonus is Bonus => !!bonus),
};
